import React, { useCallback, useEffect, useState } from 'react';
import {
    X, Zap, Thermometer, Droplets, Activity, Wind, Gauge, Info, Settings, Clock, ShieldCheck, Power, Battery, RefreshCw
} from 'lucide-react';

interface ObjectSidebarProps {
    open: boolean;
    objectName: string; // e.g. "generator_1", "chiller", "hvac_th_sensor"
    onClose: () => void;
    sensorData?: any;
    generatorData?: Record<string, any>;
    atsData?: any;
}

type LucideIcon = React.FC<any>;

// ─── Shared Components ─────────────────────────────────────────────────────────

const DetailCard: React.FC<{ icon: LucideIcon; label: string; value: any; status?: boolean; unit?: string }> = ({ icon: Icon, label, value, status, unit }) => (
    <div className="bg-gray-800/60 p-2 rounded-lg border border-gray-700/50 hover:border-gray-600 transition-all group">
        <div className="flex items-center gap-2">
            <div className="p-1 bg-gray-900/80 rounded-md group-hover:bg-blue-600/80 transition-colors shrink-0">
                <Icon className="w-3.5 h-3.5 text-blue-500 group-hover:text-white" />
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-[9px] font-bold text-gray-400 uppercase tracking-tight leading-none mb-0.5">{label}</p>
                {status !== undefined ? (
                    <div className="flex items-center gap-1">
                        <span className={`w-1.5 h-1.5 rounded-full ${status ? 'bg-emerald-500 shadow-[0_0_5px_rgba(16,185,129,0.5)]' : 'bg-red-500'}`} />
                        <span className={`text-[10px] font-bold leading-none ${status ? 'text-emerald-400' : 'text-red-400'}`}>
                            {value}
                        </span>
                    </div>
                ) : (
                    <p className="text-[10px] font-bold text-gray-100 truncate leading-none">
                        {value} <span className="text-[8px] text-gray-500 ml-0.5 font-medium">{unit}</span>
                    </p>
                )}
            </div>
        </div>
    </div>
);

// ─── Data Generators ──────────────────────────────────────────────────────────

const generateGeneratorData = (id: string) => {
    const online = Math.random() > 0.1;
    return {
        type: 'Generator',
        display_id: id.toUpperCase().replace('_', '-'),
        online,
        dg_rating: "1500 kVA",
        frequency: online ? (49.5 + Math.random() * 1).toFixed(1) : "0",
        voltage: online ? (400 + Math.random() * 15).toFixed(0) : "0",
        power_factor: online ? (0.8 + Math.random() * 0.1).toFixed(2) : "0.0",
        fuel_level: (70 + Math.random() * 25).toFixed(1),
        running_hours: (1200 + Math.random() * 500).toFixed(0),
        battery_voltage: (24.2 + Math.random() * 0.8).toFixed(1)
    };
};

const generateChillerData = (id: string) => {
    const online = Math.random() > 0.05;
    return {
        type: 'Chiller',
        display_id: id.toUpperCase().replace('_', '-'),
        online,
        set_temperature: "4.2",
        chilled_water_flow: (95 + Math.random() * 5).toFixed(2),
        condenser_water_flow: (70 + Math.random() * 5).toFixed(2),
        suction_pressure: (341000 + Math.random() * 5000).toFixed(0),
        discharge_pressure: (1754000 + Math.random() * 10000).toFixed(0),
        suction_temperature: (7.2 + Math.random() * 0.5).toFixed(1),
        discharge_temperature: (35.5 + Math.random() * 1.5).toFixed(1),
        leaving_chilled_water_temp: (3.8 + Math.random() * 0.4).toFixed(1),
        entering_chilled_water_temp: (12.2 + Math.random() * 1.0).toFixed(1),
        entering_condenser_water_temp: (37.0 + Math.random() * 0.5).toFixed(1),
        leaving_condenser_water_temp: (37.2 + Math.random() * 0.5).toFixed(1),
        cooling_load: (60 + Math.random() * 30).toFixed(0)
    };
};

const generateUPSData = (id: string) => {
    const online = Math.random() > 0.02;
    return {
        type: 'UPS System',
        display_id: id.toUpperCase().replace('_', '-'),
        online,
        current: (70 + Math.random() * 15).toFixed(2),
        frequency: (50.2 + Math.random() * 0.8).toFixed(2),
        voltage: (238 + Math.random() * 5).toFixed(1),
        power: (18000 + Math.random() * 500).toFixed(1),
        battery_status: online ? 'Charging' : 'Discharging',
        load_pct: (40 + Math.random() * 40).toFixed(0),
        runtime: (30 + Math.random() * 60).toFixed(0)
    };
};

const generateSensorData = (id: string, sensorType: 'th' | 'co2') => {
    const online = true;
    if (sensorType === 'th') {
        return {
            type: 'Temp & Humidity Sensor',
            display_id: id.toUpperCase().replace(/_/g, '-'),
            online,
            temperature: (6 + Math.random() * 6).toFixed(2),
            humidity: (40 + Math.random() * 15).toFixed(2)
        };
    }
    return {
        type: 'Carbon Dioxide Sensor',
        display_id: id.toUpperCase().replace(/_/g, '-'),
        online,
        co2_value: (700 + Math.random() * 150).toFixed(0)
    };
};

const generateCoilData = (id: string, coilType: 'cool' | 'heat') => {
    return {
        type: coilType === 'cool' ? 'Cooling Coil' : 'Heating Coil',
        display_id: id.toUpperCase().replace(/_/g, '-'),
        online: true,
        inlet_temperature: coilType === 'cool' ? (8 + Math.random() * 2).toFixed(2) : (60 + Math.random() * 10).toFixed(2),
        outlet_temperature: coilType === 'cool' ? (14 + Math.random() * 2).toFixed(2) : (68 + Math.random() * 5).toFixed(2),
        status: 'Operational'
    };
};

const generateFilterData = (id: string) => {
    return {
        type: 'Carbon Filter',
        display_id: id.toUpperCase().replace(/_/g, '-'),
        online: true,
        differential_pressure: (150 + Math.random() * 50).toFixed(0),
        status: Math.random() > 0.5 ? 'NEEDS_INSPECTION' : 'OK',
        last_inspection: '2025-08-11'
    };
};

const generateFireExtData = (id: string) => {
    const lowId = id.toLowerCase();
    const status = lowId === 'fe3' ? "EXPIRED" : "OK";

    // Deterministic random suffix based on id
    const hash = id.split('').reduce((acc, char) => (acc << 5) - acc + char.charCodeAt(0), 0);
    const suffix = Math.abs(hash)
        .toString(36)
        .toUpperCase()
        .padStart(6, '0');

    return {
        type: 'Fire Extinguisher',
        display_id: `Fire Extinguisher-${suffix}`,
        online: true,
        id: Math.floor(Math.random() * 100),
        mfg_date: "2023-12-23",
        exp_date: "2029-01-14",
        pressure_bar: (12 + Math.random() * 0.5).toFixed(2),
        weight_kg: (4.5 + Math.random() * 0.2).toFixed(2),
        last_inspection: "2025-07-29",
        status: status
    };
};

// ─── Main Component ──────────────────────────────────────────────────────────

export default function ObjectSidebar({ open, objectName, onClose, sensorData, generatorData, atsData }: ObjectSidebarProps) {
    const [data, setData] = useState<any>(null);
    console.log("[ObjectSidebar] received sensorData:", sensorData);
    console.log("[ObjectSidebar] received generatorData:", generatorData);
    console.log("[ObjectSidebar] received atsData:", atsData);

    const refreshData = useCallback(() => {
        if (!objectName) return;
        const lowName = objectName.toLowerCase();
        let newData = null;
        if (lowName.includes('chiller')) newData = generateChillerData(objectName);
        else if (lowName.includes('ups')) newData = generateUPSData(objectName);
        else if (lowName.includes('hvac_th')) newData = generateSensorData(objectName, 'th');
        else if (lowName.includes('hvac_co2')) newData = generateSensorData(objectName, 'co2');
        else if (lowName.includes('cool_coil')) newData = generateCoilData(objectName, 'cool');
        else if (lowName.includes('heat_coil')) newData = generateCoilData(objectName, 'heat');
        else if (lowName.includes('carbon_filter')) newData = generateFilterData(objectName);
        else if (lowName.startsWith('fe')) newData = generateFireExtData(objectName);
        else if (lowName.includes('generator')) {
            const genId = lowName === 'generator_1' ? 'generator_2' : 'generator_1';
            const liveData = generatorData?.[genId];
            if (liveData) {
                newData = {
                    type: 'Generator',
                    display_id: liveData.server_name.toUpperCase().replace(/_/g, '-'),
                    online: liveData.status === 'online',
                    frequency: liveData.frequency ? liveData.frequency.toFixed(3) : "-",
                    voltage: liveData.voltage ? liveData.voltage.toFixed(2) : "-",
                };
            } else {
                newData = generateGeneratorData(objectName);
            }
        }
        else if (lowName === 'ats') {
            if (atsData) {
                newData = {
                    type: 'DG Main Sensor',
                    display_id: 'DG MAIN',
                    online: atsData.status === 'online',
                    voltage: atsData.voltage ? atsData.voltage.toFixed(2) : "-",
                    frequency: atsData.frequency ? atsData.frequency.toFixed(3) : "-",
                    source: 'Main Power'
                };
            }
        }
        else if (lowName === 'sensor_display') {
            if (sensorData) {
                newData = {
                    type: 'Environmental Sensor',
                    display_id: 'SENS-DISP-01',
                    online: true,
                    temperature: sensorData.temperature,
                    humidity: sensorData.humidity,
                    last_update: sensorData.received_at
                        ? new Date(
                            sensorData.received_at.endsWith("Z")
                                ? sensorData.received_at
                                : `${sensorData.received_at}Z`
                        ).toLocaleTimeString()
                        : "-"
                };
            } else {
                newData = generateSensorData(objectName, 'th');
            }
        }
        setData(newData);
    }, [objectName, sensorData]);

    useEffect(() => {
        if (open) {
            refreshData();
            const interval = setInterval(refreshData, 2500);
            return () => clearInterval(interval);
        } else {
            setData(null);
        }
    }, [open, refreshData]);

    if (!open || !data) return null;

    const renderContent = () => {
        if (!data) return null;
        const type = data.type;

        if (type === 'Generator') {
            const d = data as any;
            return (
                <div className="grid grid-cols-2 gap-1.5">
                    <DetailCard icon={Info} label="Status" value={d.online ? 'Running' : 'Stopped'} status={d.online} />
                    <DetailCard icon={Zap} label="Voltage" value={d.voltage} unit="V" />
                    <DetailCard icon={RefreshCw} label="Frequency" value={d.frequency} unit="Hz" />
                </div>
            );
        }
        if (type === 'DG Main Sensor') {
            const d = data as any;
            return (
                <div className="grid grid-cols-2 gap-1.5">
                    <DetailCard icon={Info} label="Status" value={d.online ? 'Online' : 'Offline'} status={d.online} />
                    <DetailCard icon={Zap} label="Voltage" value={d.voltage} unit="V" />
                    <DetailCard icon={RefreshCw} label="Frequency" value={d.frequency} unit="Hz" />
                    <DetailCard icon={Activity} label="Source" value={d.source} />
                </div>
            );
        }
        if (type === 'Chiller') {
            const d = data as any;
            return (
                <div className="grid grid-cols-2 gap-1.5">
                    <DetailCard icon={Info} label="Status" value={d.online ? 'Online' : 'Offline'} status={d.online} />
                    <DetailCard icon={Settings} label="Set Temp" value={d.set_temperature} unit="°C" />
                    <DetailCard icon={Droplets} label="Chilled Flow" value={d.chilled_water_flow} unit="m³/s" />
                    <DetailCard icon={Droplets} label="Cond. Flow" value={d.condenser_water_flow} unit="m³/s" />
                    <DetailCard icon={Gauge} label="Suction Pr." value={d.suction_pressure} unit="Pa" />
                    <DetailCard icon={Gauge} label="Disch. Pr." value={d.discharge_pressure} unit="Pa" />
                    <DetailCard icon={Thermometer} label="Suction T." value={d.suction_temperature} unit="°C" />
                    <DetailCard icon={Thermometer} label="Disch. T." value={d.discharge_temperature} unit="°C" />
                    <DetailCard icon={Thermometer} label="L. Chilled" value={d.leaving_chilled_water_temp} unit="°C" />
                    <DetailCard icon={Thermometer} label="E. Chilled" value={d.entering_chilled_water_temp} unit="°C" />
                    <DetailCard icon={Thermometer} label="E. Cond." value={d.entering_condenser_water_temp} unit="°C" />
                    <DetailCard icon={Thermometer} label="L. Cond." value={d.leaving_condenser_water_temp} unit="°C" />
                    <DetailCard icon={Activity} label="Cooling Load" value={d.cooling_load} unit="%" />
                </div>
            );
        }
        if (type === 'UPS System') {
            const d = data as any;
            return (
                <div className="grid grid-cols-2 gap-1.5">
                    <DetailCard icon={Info} label="Status" value={d.online ? 'Online' : 'Offline'} status={d.online} />
                    <DetailCard icon={Zap} label="Voltage" value={d.voltage} unit="V" />
                    <DetailCard icon={Activity} label="Current" value={d.current} unit="A" />
                    <DetailCard icon={RefreshCw} label="Frequency" value={d.frequency} unit="Hz" />
                    <DetailCard icon={Power} label="Power" value={d.power} unit="kW" />
                    <DetailCard icon={Battery} label="Battery" value={d.battery_status} status={d.online} />
                    <DetailCard icon={Activity} label="Load" value={d.load_pct} unit="%" />
                    <DetailCard icon={Clock} label="Runtime" value={d.runtime} unit="min" />
                </div>
            );
        }
        if (type === 'Temp & Humidity Sensor') {
            const d = data as any;
            return (
                <div className="grid grid-cols-2 gap-1.5">
                    <DetailCard icon={Info} label="Status" value={d.online ? 'Online' : 'Offline'} status={d.online} />
                    <DetailCard icon={Thermometer} label="Temperature" value={d.temperature} unit="°C" />
                    <DetailCard icon={Droplets} label="Humidity" value={d.humidity} unit="%" />
                </div>
            );
        }
        if (type === 'Environmental Sensor') {
            const d = data as any;
            return (
                <div className="grid grid-cols-2 gap-1.5">
                    <DetailCard icon={Info} label="Status" value="Online" status={true} />
                    <DetailCard icon={Thermometer} label="Temperature" value={d.temperature} unit="°C" />
                    <DetailCard icon={Droplets} label="Humidity" value={d.humidity} unit="%" />
                    <DetailCard icon={Clock} label="Last Updated" value={d.last_update} />
                </div>
            );
        }
        if (type === 'Carbon Dioxide Sensor') {
            const d = data as any;
            return (
                <div className="grid grid-cols-2 gap-1.5">
                    <DetailCard icon={Info} label="Status" value={d.online ? 'Online' : 'Offline'} status={d.online} />
                    <DetailCard icon={Wind} label="CO2 Value" value={d.co2_value} unit="µg/m³" />
                </div>
            );
        }
        if (type.includes('Coil')) {
            const d = data as any;
            return (
                <div className="grid grid-cols-2 gap-1.5">
                    <DetailCard icon={Info} label="Status" value={d.status} status={true} />
                    <DetailCard icon={Thermometer} label="Inlet Temp" value={d.inlet_temperature} unit="°C" />
                    <DetailCard icon={Thermometer} label="Outlet Temp" value={d.outlet_temperature} unit="°C" />
                </div>
            );
        }
        if (type === 'Carbon Filter') {
            const d = data as any;
            return (
                <div className="grid grid-cols-2 gap-1.5">
                    <DetailCard icon={Gauge} label="Diff. Pressure" value={d.differential_pressure} unit="Pa" />
                    <DetailCard icon={ShieldCheck} label="Status" value={d.status} status={d.status === 'OK'} />
                    <DetailCard icon={Clock} label="Last Inspect" value={d.last_inspection || d.last_inspection_date} />
                </div>
            );
        }
        if (type === 'Fire Extinguisher') {
            const d = data as any;
            return (
                <div className="grid grid-cols-2 gap-1.5">
                    <DetailCard icon={ShieldCheck} label="Status" value={d.status} status={d.status === 'OK'} />
                    <DetailCard icon={Gauge} label="Pressure" value={d.pressure_bar} unit="bar" />
                    <DetailCard icon={Activity} label="Weight" value={d.weight_kg} unit="kg" />
                    <DetailCard icon={Clock} label="Last Inspect" value={d.last_inspection} />
                    <DetailCard icon={Info} label="Mfg Date" value={d.mfg_date} />
                    <DetailCard icon={Info} label="Exp Date" value={d.exp_date} />
                </div>
            );
        }
        return null;
    };

    return (
        <div className="fixed top-0 right-0 h-full w-[320px] bg-gray-900 shadow-2xl border-l border-white/10 z-[100] flex flex-col overflow-hidden animate-in slide-in-from-right duration-300">
            {/* Header */}
            <div className="relative h-20 flex-shrink-0 bg-gradient-to-br from-blue-900/40 to-black overflow-hidden border-b border-white/5">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_-20%,rgba(59,130,246,0.2),transparent_70%)]" />

                <div className="absolute bottom-4 left-5 right-5 flex items-end gap-3">
                    <div className="p-3 bg-blue-600 rounded-xl shadow-[0_0_20px_rgba(37,99,235,0.4)] border border-blue-400/20">
                        <Activity className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 flex flex-col gap-0.5">
                        <div className="flex items-center justify-between w-full">
                            <h2 className="text-sm font-black text-white uppercase tracking-widest leading-none mb-1">
                                {data.display_id}
                            </h2>
                            <button
                                onClick={onClose}
                                className="p-1 hover:bg-white/10 rounded-md border border-white/5 text-gray-400 hover:text-white transition-all"
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="px-1.5 py-0.5 bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded text-[9px] font-black uppercase tracking-tighter">
                                {data.type}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto custom-scroll p-4 space-y-4">
                <div>
                    <div className="px-2 py-1 flex items-center gap-1.5 mb-2">
                        <Settings className="w-3 h-3 text-blue-500" />
                        <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Technical details</h3>
                    </div>
                    {renderContent()}
                </div>
            </div>
        </div>
    );
}
